import React from 'react';
import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { appFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/styles.ts';
import { DocumentGenerationForm } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/DocumentGenerationForm.tsx';
import type {
  TFinancialConditionCertificateResponse,
  TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import type {
  TIncomeStructureByKvedCertificateResponse,
  TIncomeStructureByKvedFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import type { TCertificateSaveResult } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import type { TDocumentGenerationOptions } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import type { TApiApplicationDocument } from '~/modules/covenants/types.ts';
import type {
  StateAidCertificateType,
  TStateAidCertificateResponse,
  TStateAidFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/types.ts';
import { DOCUMENT_GENERATION_LABELS } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/constants.ts';
import { useDocumentGenerationModalState } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/hooks/useDocumentGenerationModalState.ts';

export interface IDocumentGenerationModalProps {
  open: boolean;
  documentId: string | number;
  documentName: string;
  isGenerating: boolean;
  onSaveFinancialCondition: (
    values: TFinancialConditionFormValues,
    version: number
  ) => Promise<TCertificateSaveResult>;
  onLoadFinancialCondition: () => Promise<TFinancialConditionCertificateResponse | null>;
  onSaveIncomeStructureByKved: (
    values: TIncomeStructureByKvedFormValues,
    version: number
  ) => Promise<TCertificateSaveResult>;
  onLoadIncomeStructureByKved: () => Promise<TIncomeStructureByKvedCertificateResponse | null>;
  onSaveStateAidReceived: (
    type: StateAidCertificateType,
    values: TStateAidFormValues,
    version: number
  ) => Promise<TCertificateSaveResult>;
  onLoadStateAidReceived: (
    type: StateAidCertificateType
  ) => Promise<TStateAidCertificateResponse | null>;
  stateAidCertificateType?: StateAidCertificateType;
  stateAidYearPeriod?: string;
  stateAidChildPeriod?: string;
  historicalBody?: unknown;
  regenerationSource?: TApiApplicationDocument;
  onGenerate: (options: TDocumentGenerationOptions) => Promise<boolean>;
  onClose: () => void;
}

export const DocumentGenerationModal: React.FC<IDocumentGenerationModalProps> = ({
  open,
  documentId,
  documentName,
  isGenerating,
  onSaveFinancialCondition,
  onLoadFinancialCondition,
  onSaveIncomeStructureByKved,
  onLoadIncomeStructureByKved,
  onSaveStateAidReceived,
  onLoadStateAidReceived,
  stateAidCertificateType,
  stateAidYearPeriod,
  stateAidChildPeriod,
  onGenerate,
  historicalBody,
  regenerationSource,
  onClose,
}) => {
  const {
    isFinancialCondition,
    isIncomeStructureByKved,
    isStateAidReceived,
    isBusy,
    isFormDirty,
    isGenerateDisabled,
    isSaveableCertificate,
    isSaving,
    saveTooltip,
    handleBorrowerTypeChange,
    handleFormChange,
    handleGenerate,
    handleSaveClick,
    handleSaveAttemptChange,
    handleSaveFinancialCondition,
    handleSaveIncomeStructureByKved,
    handleSaveStateAidReceived,
    setPendingHistoricalFieldNames,
    setValidationIssues,
  } = useDocumentGenerationModalState({
    open,
    documentId,
    isGenerating,
    stateAidCertificateType,
    regenerationSource,
    onClose,
    onGenerate,
    onSaveFinancialCondition,
    onSaveIncomeStructureByKved,
    onSaveStateAidReceived,
  });
  const formId = `document-generation-form-${documentId}`;

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      maxWidth={false}
      slotProps={{ paper: { sx: appFormStyles.documentGenerationDialogPaper } }}
    >
      <DialogTitle sx={appFormStyles.documentGenerationDialogTitle}>
        <Typography component="span" sx={appFormStyles.documentGenerationDialogTitleText}>
          Генерація «{documentName}»
        </Typography>

        <IconButton
          aria-label="Закрити"
          onClick={onClose}
          disabled={isBusy}
          sx={appFormStyles.documentGenerationDialogCloseButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={appFormStyles.documentGenerationDialogContent}>
        {open && (
          <DocumentGenerationForm
            documentId={documentId}
            formId={formId}
            disabled={isBusy}
            onSaveFinancialCondition={
              isFinancialCondition ? handleSaveFinancialCondition : undefined
            }
            onLoadFinancialCondition={isFinancialCondition ? onLoadFinancialCondition : undefined}
            onSaveIncomeStructureByKved={
              isIncomeStructureByKved ? handleSaveIncomeStructureByKved : undefined
            }
            onLoadIncomeStructureByKved={
              isIncomeStructureByKved ? onLoadIncomeStructureByKved : undefined
            }
            onSaveStateAidReceived={isStateAidReceived ? handleSaveStateAidReceived : undefined}
            onLoadStateAidReceived={isStateAidReceived ? onLoadStateAidReceived : undefined}
            stateAidCertificateType={stateAidCertificateType}
            stateAidYearPeriod={stateAidYearPeriod}
            stateAidChildPeriod={stateAidChildPeriod}
            onDirtyChange={isSaveableCertificate ? handleFormChange : undefined}
            onBorrowerTypeChange={isFinancialCondition ? handleBorrowerTypeChange : undefined}
            historicalBody={historicalBody}
            isHistoricalSource={Boolean(historicalBody)}
            onHistoricalPrefillPendingFieldsChange={setPendingHistoricalFieldNames}
            onValidationIssuesChange={setValidationIssues}
            onSaveAttemptChange={handleSaveAttemptChange}
          />
        )}
      </DialogContent>

      <DialogActions sx={appFormStyles.documentGenerationDialogActions}>
        {isSaveableCertificate && (
          <Tooltip
            title={<Box sx={{ whiteSpace: 'pre-line' }}>{saveTooltip}</Box>}
            disableHoverListener={!saveTooltip}
          >
            <Box sx={{ display: 'inline-flex' }}>
              <Button
                variant={isFormDirty ? 'contained' : 'outlined'}
                type="button"
                onClick={handleSaveClick}
                disabled={isBusy}
              >
                {isSaving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  DOCUMENT_GENERATION_LABELS.save
                )}
              </Button>
            </Box>
          </Tooltip>
        )}
        <Button variant="contained" onClick={handleGenerate} disabled={isGenerateDisabled}>
          {isGenerating ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            DOCUMENT_GENERATION_LABELS.generate
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
